import { useState } from 'react';
import { AiRecommendation } from '../../types';
import { ThumbsUp, ThumbsDown } from 'lucide-react';

interface AiRecommendationPanelProps {
    recommendations: AiRecommendation[];
    rootCause?: string;
    provider: string;
    cached: boolean;
    onFeedback?: (wasHelpful: boolean) => void;
}

export function AiRecommendationPanel({
    recommendations,
    rootCause,
    provider,
    cached,
    onFeedback
}: AiRecommendationPanelProps) {
    const [feedbackGiven, setFeedbackGiven] = useState(false);

    const handleFeedback = (wasHelpful: boolean) => {
        setFeedbackGiven(true);
        onFeedback?.(wasHelpful);
    };

    return (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <span className="text-2xl">🧠</span>
                    <h3 className="font-semibold text-blue-900">AI Analysis</h3>
                </div>
                <div className="flex items-center gap-2 text-xs text-blue-600">
                    {cached && <span className="bg-blue-100 px-2 py-0.5 rounded">Cached</span>}
                    <span className="uppercase">{provider}</span>
                </div>
            </div>

            {rootCause && (
                <div className="mb-4 p-4 bg-white/50 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Root Cause Hypothesis</h4>
                    <p className="text-sm text-gray-700">{rootCause}</p>
                </div>
            )}

            <h4 className="text-sm font-medium text-blue-800 mb-3">Recommended Actions</h4>
            <div className="space-y-3">
                {recommendations.map((rec, index) => (
                    <div key={index} className="flex gap-3 p-3 bg-white/70 rounded-lg">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                            {rec.order}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{rec.action}</p>
                            <p className="text-xs text-gray-600 mt-1">{rec.reason}</p>
                            <div className="flex gap-2 mt-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${rec.effort === 'low' ? 'bg-green-100 text-green-700' :
                                    rec.effort === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                    }`}>
                                    {rec.effort} effort
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded ${rec.impact === 'high' ? 'bg-green-100 text-green-700' :
                                    rec.impact === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                                    }`}>
                                    {rec.impact} impact
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {onFeedback && (
                <div className="mt-4 pt-4 border-t border-blue-200 flex items-center justify-between">
                    <span className="text-xs text-blue-600">Was this helpful?</span>
                    {feedbackGiven ? (
                        <span className="text-xs text-green-600">Thank you for your feedback!</span>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleFeedback(true)}
                                className="p-1.5 hover:bg-blue-100 rounded transition-colors"
                                title="Helpful"
                            >
                                <ThumbsUp size={16} className="text-blue-600" />
                            </button>
                            <button
                                onClick={() => handleFeedback(false)}
                                className="p-1.5 hover:bg-blue-100 rounded transition-colors"
                                title="Not helpful"
                            >
                                <ThumbsDown size={16} className="text-blue-600" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
